import { useEffect, useMemo, useRef, useState } from 'react';
import {
    IconSettings, IconX, IconCheck, IconRestore, IconDropletHalf2,
    IconTypography, IconPhoto, IconUpload, IconTrash, IconStarFilled, IconResize, IconRotate2,
    IconBrandFacebook, IconCoinTaka, IconPalette, IconAdjustmentsHorizontal,
    IconTextSize, IconFileText, IconMinus, IconPlus, IconCreditCard, IconSignature,
} from '@tabler/icons-react';
import { notifications } from '@/lib/notifications';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import Logo from '../common/Logo';
import { useAuthStore } from '../../store/authStore';
import { useOfficeSpaceStore } from '../../store/officeSpaceStore';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import {
    INVOICE_TEMPLATES, DEFAULT_INVOICE_TEMPLATE, DEFAULT_INVOICE_SETTINGS,
    SAMPLE_INVOICE, buildInvoiceHTML, PAPER_SIZES, paperPx,
} from '../../lib/invoiceTemplates';
import { buildShopServiceList } from '../../lib/incomeExpenseDefaults';
import GeoCombobox from '../common/GeoCombobox';
import SaveButton from '../common/SaveButton';
import InvoicePreviewFrame from './InvoicePreviewFrame';

// Merge stored (possibly partial) settings over the defaults so older users who
// never saved a watermark still get a complete, safe shape.
const withDefaults = (s) => ({
    ...DEFAULT_INVOICE_SETTINGS,
    ...(s || {}),
    footerServices: Array.isArray(s?.footerServices) ? s.footerServices : DEFAULT_INVOICE_SETTINGS.footerServices,
    watermark: { ...DEFAULT_INVOICE_SETTINGS.watermark, ...(s?.watermark || {}) },
});

// ── Watermark text sanitiser ──────────────────────────────────────────────────
// The final print HTML already escapes this value, but we also clean it at the
// input so a pasted <script>/tag/control-char never even reaches state: strip tag
// delimiters & control chars, collapse whitespace, and cap the length.
const WATERMARK_MAX = 60;
const sanitizeWatermarkText = (v) =>
    String(v ?? '')
        .replace(/[<>]/g, '')
        .replace(/[\x00-\x1F\x7F]/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .slice(0, WATERMARK_MAX);

// Footer text (e.g. the services the shop offers). Longer than the watermark and
// kept single-line; strips tag delimiters & control chars, collapses whitespace.
const FOOTER_TEXT_MAX = 200;
const sanitizeFooterText = (v) =>
    String(v ?? '')
        .replace(/[<>]/g, '')
        .replace(/[\x00-\x1F\x7F]/g, ' ')
        .replace(/\s{2,}/g, ' ')
        .slice(0, FOOTER_TEXT_MAX);

// ── Image optimiser (keep watermark images ≤ 100 KB) ──────────────────────────
const IMG_MAX_BYTES = 100 * 1024;
const IMG_MAX_DIM = 800;

const readFileDataUrl = (file) => new Promise((res, rej) => {
    const r = new FileReader();
    r.onload = () => res(String(r.result || ''));
    r.onerror = () => rej(new Error('read failed'));
    r.readAsDataURL(file);
});

const loadImage = (src) => new Promise((res, rej) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => rej(new Error('decode failed'));
    img.src = src;
});

// Approx. decoded byte size of a data-URL (base64 → bytes).
const dataUrlBytes = (u) => {
    const i = u.indexOf(',');
    return i < 0 ? u.length : Math.floor((u.length - i - 1) * 3 / 4);
};

// Re-encode as WebP (preserves transparency) at a shrinking size/quality until it
// fits under maxBytes. Images already under the cap are returned untouched.
async function compressImage(file, maxBytes = IMG_MAX_BYTES) {
    const original = await readFileDataUrl(file);
    if (dataUrlBytes(original) <= maxBytes) return original;
    const img = await loadImage(original);
    const longest = Math.max(img.width, img.height) || IMG_MAX_DIM;
    let dim = Math.min(IMG_MAX_DIM, longest);
    const encodeAt = (size) => {
        const r = size / longest;
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(img.width * r));
        canvas.height = Math.max(1, Math.round(img.height * r));
        canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
        return canvas;
    };
    let last = original;
    for (let pass = 0; pass < 6; pass++) {
        const canvas = encodeAt(dim);
        for (const q of [0.85, 0.7, 0.55, 0.4, 0.3]) {
            const out = canvas.toDataURL('image/webp', q);
            last = out;
            if (dataUrlBytes(out) <= maxBytes) return out;
        }
        dim = Math.round(dim * 0.75); // shrink further and retry
    }
    return last; // smallest we could produce
}

/**
 * InvoiceSettings — a gear button + popup for the invoice tab that controls the
 * invoice's appearance in one place: theme (template), font-size scaling
 * (80–150%) and a text/image watermark. Selections are staged locally and
 * committed together to `user.settings` (invoiceTemplate + invoiceSettings) on
 * "Apply", so preview / print / download all pick them up.
 */
// Uncontrolled by default (renders its own "সেটিংস" button). Pass `open` + `onOpenChange` to drive it from elsewhere
// (e.g. a tile on the Settings page) — the built-in button is then hidden.
export default function InvoiceSettings({ shop = {}, open: controlledOpen, onOpenChange }) {
    const { t, currentLanguage } = useTranslation();
    const lang = currentLanguage === 'en' ? 'en' : 'bn';
    const tplName = (tpl) => (lang === 'en' ? (tpl?.nameEn || tpl?.name) : tpl?.name);

    const user = useAuthStore((s) => s.user);
    const updateSettings = useAuthStore((s) => s.updateSettings);
    const selectedOfficeSpace = useOfficeSpaceStore((s) => s.selectedOfficeSpace);

    const savedTemplate = user?.settings?.invoiceTemplate || DEFAULT_INVOICE_TEMPLATE;
    const savedSettings = useMemo(() => withDefaults(user?.settings?.invoiceSettings), [user?.settings?.invoiceSettings]);

    const isControlled = controlledOpen !== undefined;
    const [innerOpen, setInnerOpen] = useState(false);
    const open = isControlled ? !!controlledOpen : innerOpen;
    const setOpen = (v) => { if (!isControlled) setInnerOpen(v); onOpenChange?.(v); };
    const [saving, setSaving] = useState(false);
    const [imgBusy, setImgBusy] = useState(false);
    const [sigBusy, setSigBusy] = useState(false);
    const [template, setTemplate] = useState(savedTemplate);
    const [settings, setSettings] = useState(savedSettings);
    const [payMethod, setPayMethod] = useState('');
    const [payNumber, setPayNumber] = useState('');
    const fileRef = useRef(null);
    const sigFileRef = useRef(null);

    // Payment-method logo (settings chip) — same slugs the invoice uses; missing
    // files hide themselves via onError.
    const PAYMENT_LOGO = {
        'বিকাশ': 'bkash', 'নগদ': 'nagad', 'রকেট': 'rocket', 'উপায়': 'upay', 'ব্যাংক': 'bank', 'ক্যাশ': 'cash',
        bkash: 'bkash', nagad: 'nagad', rocket: 'rocket', upay: 'upay', bank: 'bank', cash: 'cash',
    };
    const paymentLogoSrc = (m) => {
        const k = String(m || '').trim();
        const slug = PAYMENT_LOGO[k] || PAYMENT_LOGO[k.toLowerCase()];
        return slug ? `/images/payment/${slug}.svg` : null;
    };

    // Footer suggestions = the shop's full service list (auto modules + profile
    // services), names only. The user may also type any custom text.
    const footerServiceOptions = useMemo(
        () => buildShopServiceList(user?.settings, selectedOfficeSpace?.businessProfile)
            .map((s) => ({ label: s.name, value: s.name })),
        [user?.settings, selectedOfficeSpace?.businessProfile],
    );

    const FOOTER_SERVICE_MAX = 20;
    const addFooterService = (name) => {
        const clean = sanitizeFooterText(name).trim();
        if (!clean) return;
        const key = clean.normalize('NFC').toLowerCase();
        setSettings((s) => {
            const list = Array.isArray(s.footerServices) ? s.footerServices : [];
            if (list.length >= FOOTER_SERVICE_MAX) return s;
            if (list.some((x) => String(x).normalize('NFC').toLowerCase() === key)) return s;
            return { ...s, footerServices: [...list, clean] };
        });
        // The GeoCombobox clears itself via the `key` remount on each add.
    };
    const removeFooterService = (idx) =>
        setSettings((s) => ({ ...s, footerServices: (s.footerServices || []).filter((_, i) => i !== idx) }));

    // Payment numbers (bKash / Nagad / Rocket / …) → shown in the invoice footer.
    const PAYMENT_PRESETS = ['বিকাশ', 'নগদ', 'রকেট', 'উপায়', 'ব্যাংক', 'ক্যাশ'];
    const PAYMENT_MAX = 12;
    const addPayment = () => {
        const m = sanitizeFooterText(payMethod).trim();
        const n = sanitizeFooterText(payNumber).trim();
        if (!n) return;
        setSettings((s) => {
            const list = Array.isArray(s.paymentMethods) ? s.paymentMethods : [];
            if (list.length >= PAYMENT_MAX) return s;
            return { ...s, paymentMethods: [...list, { method: m, number: n }] };
        });
        setPayMethod('');
        setPayNumber('');
    };
    const removePayment = (idx) =>
        setSettings((s) => ({ ...s, paymentMethods: (s.paymentMethods || []).filter((_, i) => i !== idx) }));

    // Re-seed the staged values from the saved ones every time the popup opens.
    useEffect(() => {
        if (open) { setTemplate(savedTemplate); setSettings(savedSettings); }
    }, [open, savedTemplate, savedSettings]);

    // Hide the floating WhatsApp button while this full-screen modal is open so it
    // never overlaps the modal's controls (e.g. the Apply button).
    useEffect(() => {
        window.dispatchEvent(new Event(open ? 'sohozkaj:hide-whatsapp' : 'sohozkaj:show-whatsapp'));
        return () => window.dispatchEvent(new Event('sohozkaj:show-whatsapp'));
    }, [open]);

    // The receipt/slip theme (1f) prints on a 3-inch thermal roll — keep the paper
    // size in sync so it doesn't stay on A4, and restore A4 when leaving the slip.
    useEffect(() => {
        setSettings((s) => {
            if (template === '1f' && s.paperSize !== 'slip') return { ...s, paperSize: 'slip' };
            if (template !== '1f' && s.paperSize === 'slip') return { ...s, paperSize: 'a4' };
            return s;
        });
    }, [template]);

    const wm = settings.watermark;
    const setWm = (patch) => setSettings((s) => ({ ...s, watermark: { ...s.watermark, ...patch } }));

    const dirty =
        template !== savedTemplate ||
        JSON.stringify(settings) !== JSON.stringify(savedSettings);

    // Real shop identity threaded into the sample invoice so previews (both the
    // big one and the theme thumbnails) show the actual name / logo / Facebook.
    const previewShop = useMemo(() => ({
        ...SAMPLE_INVOICE.shop,
        shopName: shop.shopName || SAMPLE_INVOICE.shop.shopName,
        shopLogo: shop.shopLogo || '',
        facebookUrl: shop.facebookUrl || SAMPLE_INVOICE.shop.facebookUrl,
    }), [shop.shopName, shop.shopLogo, shop.facebookUrl]);

    const sampleData = useMemo(() => ({
        ...SAMPLE_INVOICE,
        shop: previewShop,
        footerServicesLabel: t('invoiceSettings.footerServicesLabel') || 'আমাদের সেবা সমূহ',
    }), [previewShop, t]);

    // Big live preview reflects the staged template + all settings.
    const previewHtml = useMemo(
        () => buildInvoiceHTML(template, sampleData, settings),
        [template, settings, sampleData],
    );

    // Theme thumbnails — one mini preview per template. Built with baseline
    // appearance (default font, no watermark) so switching font/watermark never
    // rebuilds them; they represent each design's identity, not the live tweaks.
    const themeThumbs = useMemo(
        () => INVOICE_TEMPLATES.map((tpl) => ({
            id: tpl.id,
            name: tplName(tpl),
            html: buildInvoiceHTML(tpl.id, sampleData, { fontScale: 100, showAmountInWords: true, showFacebook: true }),
        })),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [sampleData, lang],
    );

    const onPickImage = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            notifications.show({ message: t('invoiceSettings.imageOnly') || 'ছবি ফাইল নির্বাচন করুন', color: 'red' });
            return;
        }
        setImgBusy(true);
        try {
            // Optimise down to ≤ 100 KB before storing (it's persisted in user settings).
            setWm({ image: await compressImage(file) });
        } catch {
            notifications.show({ message: t('invoiceSettings.imageFailed') || 'ছবি প্রসেস করা যায়নি', color: 'red' });
        } finally {
            setImgBusy(false);
        }
    };

    const onPickSignature = async (e) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!file.type.startsWith('image/')) {
            notifications.show({ message: t('invoiceSettings.imageOnly') || 'ছবি ফাইল নির্বাচন করুন', color: 'red' });
            return;
        }
        setSigBusy(true);
        try {
            setSettings((s) => ({ ...s, signatureImage: '' })); // clear stale while processing
            const img = await compressImage(file);
            setSettings((s) => ({ ...s, signatureImage: img, showSignature: true }));
        } catch {
            notifications.show({ message: t('invoiceSettings.imageFailed') || 'ছবি প্রসেস করা যায়নি', color: 'red' });
        } finally {
            setSigBusy(false);
        }
    };

    const apply = async () => {
        if (saving) return;
        if (!dirty) { setOpen(false); return; }
        setSaving(true);
        try {
            await updateSettings({ invoiceTemplate: template, invoiceSettings: settings });
            notifications.show({ title: t('common.success'), message: t('invoiceSettings.saved') || 'সেটিংস সেভ হয়েছে', color: 'green' });
            setOpen(false);
        } catch (err) {
            notifications.show({ title: t('common.error'), message: err.message, color: 'red' });
        } finally {
            setSaving(false);
        }
    };

    const resetAll = () => { setTemplate(DEFAULT_INVOICE_TEMPLATE); setSettings(withDefaults()); };

    // Shared on/off switch (matches the watermark toggle style).
    const renderSwitch = (checked, onChange) => (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            onClick={onChange}
            className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600'}`}
        >
            <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${checked ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
        </button>
    );

    return (
        <>
            {!isControlled && (
                <button
                    type="button"
                    onClick={() => setOpen(true)}
                    className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-zinc-900 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-xs sm:text-sm font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                >
                    <IconSettings size={16} />
                    {t('invoiceSettings.button') || 'সেটিংস'}
                </button>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent
                    showClose={false}
                    className="p-0 overflow-hidden flex flex-col rounded-none border-0"
                    style={{ width: '100vw', maxWidth: '100vw', height: '100vh', maxHeight: '100vh' }}
                >
                    {/* Header — brand-tinted, with the language-aware SohozKaj logo */}
                    <div className="flex items-center justify-between gap-4 px-5 py-3.5 border-b border-orange-100/70 dark:border-zinc-800 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent dark:from-primary/10 dark:via-zinc-900 dark:to-zinc-900 flex-none">
                        <div className="flex items-center gap-3">
                            <Logo w={104} />
                            <span className="h-6 w-px bg-orange-200/70 dark:bg-zinc-700" />
                            <div className="flex items-center gap-2">
                                <IconSettings size={18} className="text-primary" />
                                <DialogTitle className="text-base font-semibold text-zinc-800 dark:text-zinc-100">
                                    {t('invoiceSettings.title') || 'ইনভয়েস সেটিংস'}
                                </DialogTitle>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={() => setOpen(false)}
                            aria-label={t('common.close') || 'বন্ধ'}
                            className="inline-flex h-9 w-9 flex-none items-center justify-center rounded-lg bg-red-500 text-white hover:bg-red-600 transition-colors"
                        >
                            <IconX size={18} />
                        </button>
                    </div>

                    {/* Body — controls (left, scrolls) + big live preview (right).
                        Full-page height; the left column scrolls internally. */}
                    <div className="flex-1 min-h-0 flex flex-col lg:flex-row overflow-hidden">
                        <div className="flex-1 min-w-0 overflow-y-auto p-4 sm:p-6 space-y-5">

                            {/* Theme — visual picker with mini live previews */}
                            <section>
                                <div className="flex items-center gap-2 mb-3">
                                    <IconPalette size={17} className="text-primary" />
                                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                        {t('invoiceSettings.theme') || 'থিম'}
                                    </h3>
                                    <span className="ml-auto inline-flex items-center gap-1 text-[11px] text-zinc-400 dark:text-zinc-500">
                                        <IconStarFilled size={10} className="text-primary" />
                                        {t('invoiceSettings.defaultHint') || 'নির্বাচিত থিম ডিফল্ট হিসেবে সেভ হবে'}
                                    </span>
                                </div>
                                {/* Container-driven auto-fill: columns size to the LEFT panel's
                                    actual width (which halves once the preview docks beside it at
                                    lg), so thumbnails never get crushed to viewport-based breakpoints.
                                    Each track ≈ the A4 sheet's rendered width so it fills its cell. */}
                                <div className="grid gap-2 grid-cols-[repeat(auto-fill,minmax(116px,1fr))]">
                                    {themeThumbs.map((thumb) => {
                                        const active = template === thumb.id;
                                        const isDefault = savedTemplate === thumb.id;
                                        return (
                                            <button
                                                key={thumb.id}
                                                type="button"
                                                onClick={() => setTemplate(thumb.id)}
                                                aria-pressed={active}
                                                title={thumb.name}
                                                className={`group relative flex flex-col overflow-hidden rounded-lg border text-left transition-all ${
                                                    active
                                                        ? 'border-primary ring-2 ring-primary/40 shadow-md'
                                                        : 'border-zinc-200 dark:border-zinc-700 hover:border-primary/50 hover:shadow-sm'
                                                }`}
                                            >
                                                {/* Fixed-height thumb that always shows the WHOLE page (no bottom crop on wide screens). */}
                                                <div className="relative h-[170px] overflow-hidden bg-zinc-100 dark:bg-zinc-800 py-1.5 pointer-events-none">
                                                    <InvoicePreviewFrame
                                                        html={thumb.html}
                                                        debounceMs={0}
                                                        paper={paperPx(thumb.id === '1f' ? 'slip' : 'a4')}
                                                        fitHeight={158}
                                                    />
                                                </div>
                                                {isDefault && (
                                                    <span className="absolute left-1 top-1 inline-flex items-center gap-0.5 rounded-full bg-primary px-1.5 py-0.5 text-[9px] font-bold text-white shadow">
                                                        <IconStarFilled size={8} />
                                                        {t('invoiceSettings.default') || 'ডিফল্ট'}
                                                    </span>
                                                )}
                                                <div className={`flex items-center justify-between gap-1 border-t px-2 py-1.5 ${active ? 'border-primary/20 bg-primary/5' : 'border-zinc-100 bg-white dark:border-zinc-700 dark:bg-zinc-800'}`}>
                                                    <span className={`truncate text-[11px] font-semibold ${active ? 'text-primary' : 'text-zinc-700 dark:text-zinc-200'}`}>{thumb.name}</span>
                                                    {active
                                                        ? <IconCheck size={13} className="flex-none text-primary" />
                                                        : <span className="h-3 w-3 flex-none rounded-full border border-zinc-300 dark:border-zinc-600" />}
                                                </div>
                                            </button>
                                        );
                                    })}
                                </div>
                            </section>

                            {/* Theme options + Global settings, side by side on wide screens */}
                            <div className="grid xl:grid-cols-2 gap-5 items-start">
                            <div className="space-y-5">
                            {/* ── Theme options (card) ── */}
                            <section className="rounded-xl border border-zinc-200 dark:border-zinc-700/70 bg-zinc-50/60 dark:bg-zinc-800/40 p-4 space-y-4">
                                <div className="flex items-center gap-2">
                                    <IconAdjustmentsHorizontal size={16} className="text-primary" />
                                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                        {t('invoiceSettings.themeOptions') || 'থিম অপশন'}
                                    </h3>
                                </div>

                                {/* Font size — compact stepper + quick presets */}
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <IconTextSize size={16} className="text-zinc-400 dark:text-zinc-500" />
                                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{t('invoiceSettings.fontSize') || 'ফন্ট সাইজ'}</span>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <div className="hidden sm:flex items-center gap-1">
                                            {[
                                                { v: 90, label: t('invoiceSettings.fontSmall') || 'ছোট' },
                                                { v: 100, label: t('invoiceSettings.fontNormal') || 'মাঝারি' },
                                                { v: 120, label: t('invoiceSettings.fontLarge') || 'বড়' },
                                            ].map((p) => (
                                                <button
                                                    key={p.v}
                                                    type="button"
                                                    onClick={() => setSettings((s) => ({ ...s, fontScale: p.v }))}
                                                    className={`px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors ${settings.fontScale === p.v ? 'bg-primary border-primary text-white' : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-500 dark:text-zinc-400 hover:border-primary/50'}`}
                                                >
                                                    {p.label}
                                                </button>
                                            ))}
                                        </div>
                                        <div className="flex items-center rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800">
                                            <button
                                                type="button"
                                                aria-label={t('invoiceSettings.fontSmaller') || 'ছোট করুন'}
                                                onClick={() => setSettings((s) => ({ ...s, fontScale: Math.max(80, s.fontScale - 5) }))}
                                                disabled={settings.fontScale <= 80}
                                                className="h-8 w-8 flex items-center justify-center text-zinc-500 hover:text-primary disabled:opacity-40 disabled:hover:text-zinc-500"
                                            >
                                                <IconMinus size={14} />
                                            </button>
                                            <span className="w-12 text-center text-xs font-bold text-primary tabular-nums">{settings.fontScale}%</span>
                                            <button
                                                type="button"
                                                aria-label={t('invoiceSettings.fontLarger') || 'বড় করুন'}
                                                onClick={() => setSettings((s) => ({ ...s, fontScale: Math.min(150, s.fontScale + 5) }))}
                                                disabled={settings.fontScale >= 150}
                                                className="h-8 w-8 flex items-center justify-center text-zinc-500 hover:text-primary disabled:opacity-40 disabled:hover:text-zinc-500"
                                            >
                                                <IconPlus size={14} />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                {/* Paper size — drives the print/download page size */}
                                <div className="flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex items-center gap-1.5">
                                        <IconFileText size={16} className="text-zinc-400 dark:text-zinc-500" />
                                        <span className="text-sm font-medium text-zinc-700 dark:text-zinc-200">{t('invoiceSettings.paperSize') || 'কাগজের সাইজ'}</span>
                                    </div>
                                    <div className="flex flex-wrap gap-1.5 justify-end">
                                        {(template === '1f' ? PAPER_SIZES.filter((p) => p.id === 'slip') : PAPER_SIZES.filter((p) => p.id !== 'slip')).map((p) => {
                                            const active = (settings.paperSize || 'a4') === p.id;
                                            const label = { a4: 'A4', letter: 'Letter', a5: 'A5', slip: (t('invoiceSettings.paperSlip') || 'স্লিপ ৩″') }[p.id] || p.id;
                                            return (
                                                <button
                                                    key={p.id}
                                                    type="button"
                                                    onClick={() => setSettings((s) => ({ ...s, paperSize: p.id }))}
                                                    className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-colors ${
                                                        active
                                                            ? 'bg-primary border-primary text-white'
                                                            : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:border-primary/50'
                                                    }`}
                                                >
                                                    {active && <IconCheck size={12} />}
                                                    {label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                            </section>
                            {/* Signature (above watermark) */}
                            <section className="rounded-xl border border-zinc-200 dark:border-zinc-700/70 bg-zinc-50/60 dark:bg-zinc-800/40 p-4">
                                <div className="flex items-center justify-between mb-2.5">
                                    <h3 className="inline-flex items-center gap-1.5 text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                        <IconSignature size={16} className="text-primary" />
                                        {t('invoiceSettings.signature') || 'স্বাক্ষর'}
                                    </h3>
                                    {renderSwitch(
                                        settings.showSignature === true,
                                        () => setSettings((s) => ({ ...s, showSignature: s.showSignature !== true })),
                                    )}
                                </div>
                                {settings.showSignature && (
                                    <div className="flex items-center gap-3">
                                        <div className="h-14 w-24 flex-none rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center overflow-hidden">
                                            {settings.signatureImage
                                                ? <img src={settings.signatureImage} alt="" className="max-h-full max-w-full object-contain" />
                                                : <IconSignature size={22} className="text-zinc-300 dark:text-zinc-600" />}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="flex items-center gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => sigFileRef.current?.click()}
                                                    disabled={sigBusy}
                                                    className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-xs font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                >
                                                    {sigBusy
                                                        ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                        : <IconUpload size={13} />}
                                                    {t('invoiceSettings.uploadSignature') || 'স্বাক্ষর আপলোড'}
                                                </button>
                                                {settings.signatureImage && (
                                                    <button
                                                        type="button"
                                                        onClick={() => setSettings((s) => ({ ...s, signatureImage: '' }))}
                                                        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-red-500 text-xs font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                    >
                                                        <IconTrash size={13} />
                                                        {t('invoiceSettings.removeImage') || 'সরান'}
                                                    </button>
                                                )}
                                            </div>
                                            <p className="mt-1.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                {t('invoiceSettings.signatureHint') || 'ইনভয়েসে স্বাক্ষরের লাইনের উপরে দেখাবে।'}
                                            </p>
                                        </div>
                                        <input ref={sigFileRef} type="file" accept="image/*" onChange={onPickSignature} className="hidden" />
                                    </div>
                                )}
                            </section>

                            {/* Watermark (left column) */}
                            <section className="rounded-xl border border-zinc-200 dark:border-zinc-700/70 bg-zinc-50/60 dark:bg-zinc-800/40 p-4">
                                <div className="flex items-center justify-between mb-2.5">
                                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                        {t('invoiceSettings.watermark') || 'ওয়াটারমার্ক'}
                                    </h3>
                                    <button
                                        type="button"
                                        role="switch"
                                        aria-checked={wm.enabled}
                                        onClick={() => setWm({ enabled: !wm.enabled })}
                                        className={`relative inline-flex h-6 w-11 flex-none items-center rounded-full transition-colors ${wm.enabled ? 'bg-primary' : 'bg-zinc-300 dark:bg-zinc-600'}`}
                                    >
                                        <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${wm.enabled ? 'translate-x-[22px]' : 'translate-x-0.5'}`} />
                                    </button>
                                </div>

                                {wm.enabled && (
                                    <div className="space-y-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700 p-3.5">
                                        {/* Type: text / image */}
                                        <div className="grid grid-cols-2 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                                            {[
                                                { id: 'text', icon: <IconTypography size={14} />, label: t('invoiceSettings.typeText') || 'টেক্সট' },
                                                { id: 'image', icon: <IconPhoto size={14} />, label: t('invoiceSettings.typeImage') || 'ছবি' },
                                            ].map((opt) => (
                                                <button
                                                    key={opt.id}
                                                    type="button"
                                                    onClick={() => setWm({ type: opt.id })}
                                                    className={`inline-flex items-center justify-center gap-1.5 py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                                        wm.type === opt.id
                                                            ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm'
                                                            : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                                                    }`}
                                                >
                                                    {opt.icon}{opt.label}
                                                </button>
                                            ))}
                                        </div>

                                        {wm.type === 'text' ? (
                                            <div>
                                                <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                                                    {t('invoiceSettings.watermarkText') || 'ওয়াটারমার্ক টেক্সট'}
                                                </label>
                                                <input
                                                    type="text"
                                                    value={wm.text}
                                                    maxLength={WATERMARK_MAX}
                                                    onChange={(e) => setWm({ text: sanitizeWatermarkText(e.target.value) })}
                                                    onPaste={(e) => {
                                                        e.preventDefault();
                                                        const pasted = e.clipboardData.getData('text');
                                                        setWm({ text: sanitizeWatermarkText(wm.text + pasted) });
                                                    }}
                                                    placeholder={shop.shopName || t('invoiceSettings.watermarkTextPlaceholder') || 'দোকানের নাম'}
                                                    className="w-full px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                                />
                                                <p className="mt-1.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                    {t('invoiceSettings.watermarkTextHint') || 'খালি রাখলে দোকানের নাম ব্যবহৃত হবে।'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div>
                                                <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                                                    {t('invoiceSettings.watermarkImage') || 'ওয়াটারমার্ক ছবি'}
                                                </label>
                                                <div className="flex items-center gap-3">
                                                    <div className="h-14 w-14 flex-none rounded-lg border border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-800 flex items-center justify-center overflow-hidden">
                                                        {(wm.image || shop.shopLogo)
                                                            ? <img src={wm.image || shop.shopLogo} alt="" className="max-h-full max-w-full object-contain" />
                                                            : <IconPhoto size={20} className="text-zinc-300 dark:text-zinc-600" />}
                                                    </div>
                                                    <div className="min-w-0">
                                                        <div className="flex items-center gap-2">
                                                            <button
                                                                type="button"
                                                                onClick={() => fileRef.current?.click()}
                                                                disabled={imgBusy}
                                                                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 text-xs font-semibold rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                                            >
                                                                {imgBusy
                                                                    ? <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                                                                    : <IconUpload size={13} />}
                                                                {t('invoiceSettings.uploadImage') || 'ছবি আপলোড'}
                                                            </button>
                                                            {wm.image && (
                                                                <button
                                                                    type="button"
                                                                    onClick={() => setWm({ image: '' })}
                                                                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-red-500 text-xs font-semibold rounded-lg hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                                                >
                                                                    <IconTrash size={13} />
                                                                    {t('invoiceSettings.removeImage') || 'সরান'}
                                                                </button>
                                                            )}
                                                        </div>
                                                        <p className="mt-1.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                            {wm.image
                                                                ? (t('invoiceSettings.customImage') || 'কাস্টম ছবি ব্যবহৃত হচ্ছে')
                                                                : shop.shopLogo
                                                                    ? (t('invoiceSettings.useShopLogo') || 'দোকানের লোগো ব্যবহৃত হচ্ছে')
                                                                    : (t('invoiceSettings.noLogo') || 'কোনো লোগো নেই — একটি ছবি আপলোড করুন')}
                                                        </p>
                                                    </div>
                                                    <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" />
                                                </div>
                                            </div>
                                        )}

                                        {/* Opacity */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                                    <IconDropletHalf2 size={14} />
                                                    {t('invoiceSettings.opacity') || 'স্বচ্ছতা'}
                                                </label>
                                                <span className="text-xs font-bold text-primary tabular-nums">{wm.opacity}%</span>
                                            </div>
                                            <input
                                                type="range"
                                                min={3}
                                                max={40}
                                                step={1}
                                                value={wm.opacity}
                                                onChange={(e) => setWm({ opacity: Number(e.target.value) })}
                                                className="w-full accent-primary cursor-pointer"
                                            />
                                        </div>

                                        {/* Size */}
                                        <div>
                                            <div className="flex items-center justify-between mb-1.5">
                                                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400">
                                                    <IconResize size={14} />
                                                    {t('invoiceSettings.wmSize') || 'সাইজ'}
                                                </label>
                                                <span className="text-xs font-bold text-primary tabular-nums">{wm.size ?? 55}</span>
                                            </div>
                                            <input
                                                type="range"
                                                min={20}
                                                max={100}
                                                step={1}
                                                value={wm.size ?? 55}
                                                onChange={(e) => setWm({ size: Number(e.target.value) })}
                                                className="w-full accent-primary cursor-pointer"
                                            />
                                        </div>

                                        {/* Position (9-point grid) + Rotation */}
                                        <div className="flex items-start justify-between gap-4 flex-wrap">
                                            <div>
                                                <label className="block text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                                                    {t('invoiceSettings.wmPosition') || 'অবস্থান'}
                                                </label>
                                                <div className="grid grid-cols-3 gap-1 w-[104px]">
                                                    {['top-left', 'top', 'top-right', 'left', 'center', 'right', 'bottom-left', 'bottom', 'bottom-right'].map((pos) => {
                                                        const active = (wm.position || 'center') === pos;
                                                        return (
                                                            <button
                                                                key={pos}
                                                                type="button"
                                                                onClick={() => setWm({ position: pos })}
                                                                aria-label={pos}
                                                                aria-pressed={active}
                                                                className={`h-7 rounded-md border flex items-center justify-center transition-colors ${
                                                                    active
                                                                        ? 'bg-primary border-primary'
                                                                        : 'bg-white dark:bg-zinc-800 border-zinc-200 dark:border-zinc-700 hover:border-primary/50'
                                                                }`}
                                                            >
                                                                <span className={`h-1.5 w-1.5 rounded-full ${active ? 'bg-white' : 'bg-zinc-300 dark:bg-zinc-600'}`} />
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                            <div className="min-w-[140px] flex-1">
                                                <label className="inline-flex items-center gap-1.5 text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1.5">
                                                    <IconRotate2 size={14} />
                                                    {t('invoiceSettings.wmRotation') || 'ঘোরানো'}
                                                </label>
                                                <div className="grid grid-cols-3 gap-1.5 p-1 rounded-lg bg-zinc-100 dark:bg-zinc-800">
                                                    {[
                                                        { v: 0, label: t('invoiceSettings.wmRotFlat') || 'সোজা' },
                                                        { v: -30, label: '−৩০°' },
                                                        { v: -45, label: '−৪৫°' },
                                                    ].map((opt) => {
                                                        const active = (wm.rotation ?? -30) === opt.v;
                                                        return (
                                                            <button
                                                                key={opt.v}
                                                                type="button"
                                                                onClick={() => setWm({ rotation: opt.v })}
                                                                className={`py-1.5 rounded-md text-xs font-semibold transition-colors ${
                                                                    active
                                                                        ? 'bg-white dark:bg-zinc-900 text-primary shadow-sm'
                                                                        : 'text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200'
                                                                }`}
                                                            >
                                                                {opt.label}
                                                            </button>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}
                            </section>
                            </div>

                            {/* ── Global settings (card) ── */}
                            <section className="rounded-xl border border-zinc-200 dark:border-zinc-700/70 bg-zinc-50/60 dark:bg-zinc-800/40 p-4 space-y-4">
                                <div className="flex items-center gap-2">
                                    <IconSettings size={16} className="text-primary" />
                                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                        {t('invoiceSettings.globalSettings') || 'গ্লোবাল সেটিংস'}
                                    </h3>
                                </div>

                            {/* Amount in words (কথায়) */}
                            <div>
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5">
                                        <IconCoinTaka size={19} className="mt-0.5 flex-none text-zinc-400 dark:text-zinc-500" />
                                        <div>
                                            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                                                {t('invoiceSettings.amountInWords') || 'টাকার পরিমাণ কথায়'}
                                            </p>
                                            <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                {t('invoiceSettings.amountInWordsHint') || 'ইনভয়েসে মোট টাকার পরিমাণ কথায় (বাংলায়) দেখাবে।'}
                                            </p>
                                        </div>
                                    </div>
                                    {renderSwitch(
                                        settings.showAmountInWords !== false,
                                        () => setSettings((s) => ({ ...s, showAmountInWords: s.showAmountInWords === false })),
                                    )}
                                </div>
                            </div>

                            {/* Show Facebook page in the header */}
                            <div className="border-t border-zinc-200/60 dark:border-zinc-700/50 pt-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5">
                                        <IconBrandFacebook size={19} className="mt-0.5 flex-none text-zinc-400 dark:text-zinc-500" />
                                        <div>
                                            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                                                {t('invoiceSettings.showFacebook') || 'ফেসবুক পেজ দেখান'}
                                            </p>
                                            <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                {shop.facebookUrl
                                                    ? (t('invoiceSettings.showFacebookHint') || 'ইনভয়েসের হেডারে দোকানের ফেসবুক পেজ দেখাবে।')
                                                    : (t('invoiceSettings.noFacebook') || 'বিজনেস প্রোফাইলে ফেসবুক পেজ যোগ করা নেই।')}
                                            </p>
                                        </div>
                                    </div>
                                    {renderSwitch(
                                        settings.showFacebook !== false,
                                        () => setSettings((s) => ({ ...s, showFacebook: s.showFacebook === false })),
                                    )}
                                </div>
                            </div>

                            {/* Show the shop logo inside the invoice */}
                            <div className="border-t border-zinc-200/60 dark:border-zinc-700/50 pt-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5">
                                        <IconPhoto size={19} className="mt-0.5 flex-none text-zinc-400 dark:text-zinc-500" />
                                        <div>
                                            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                                                {t('invoiceSettings.showLogo') || 'লোগো দেখান'}
                                            </p>
                                            <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                {shop.shopLogo
                                                    ? (t('invoiceSettings.showLogoHint') || 'ইনভয়েসের হেডারে দোকানের লোগো দেখাবে।')
                                                    : (t('invoiceSettings.noLogoSet') || 'বিজনেস প্রোফাইলে কোনো লোগো যোগ করা নেই।')}
                                            </p>
                                        </div>
                                    </div>
                                    {renderSwitch(
                                        settings.showLogo !== false,
                                        () => setSettings((s) => ({ ...s, showLogo: s.showLogo === false })),
                                    )}
                                </div>
                            </div>

                            {/* Show the selected customer's earlier unpaid dues (running balance) */}
                            <div className="border-t border-zinc-200/60 dark:border-zinc-700/50 pt-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="flex items-start gap-2.5">
                                        <IconCoinTaka size={19} className="mt-0.5 flex-none text-zinc-400 dark:text-zinc-500" />
                                        <div>
                                            <p className="text-sm font-medium text-zinc-800 dark:text-zinc-100">
                                                {t('invoiceSettings.showPreviousDue') || 'পূর্বের বকেয়া দেখান'}
                                            </p>
                                            <p className="mt-0.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                                {t('invoiceSettings.showPreviousDueHint') || 'নির্বাচিত গ্রাহকের আগের ইনভয়েসের বকেয়া এই ইনভয়েসে যোগ হবে।'}
                                            </p>
                                        </div>
                                    </div>
                                    {renderSwitch(
                                        settings.showPreviousDue === true,
                                        () => setSettings((s) => ({ ...s, showPreviousDue: s.showPreviousDue !== true })),
                                    )}
                                </div>
                            </div>

                            {/* Payment numbers (bKash / Nagad / …) shown in the footer */}
                            <div className="border-t border-zinc-200/60 dark:border-zinc-700/50 pt-4">
                                <div className="flex items-center gap-2 mb-2.5">
                                    <IconCreditCard size={18} className="flex-none text-zinc-400 dark:text-zinc-500" />
                                    <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100">
                                        {t('invoiceSettings.paymentNumbers') || 'পেমেন্ট নম্বর'}
                                    </h3>
                                </div>
                                {(settings.paymentMethods?.length > 0) && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {settings.paymentMethods.map((p, idx) => (
                                            <span key={`${p.method}-${p.number}-${idx}`} className="inline-flex items-center gap-1 pl-2 pr-1 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                                                {paymentLogoSrc(p.method) && (
                                                    <img src={paymentLogoSrc(p.method)} alt="" onError={(e) => { e.currentTarget.style.display = 'none'; }} className="h-3.5 w-auto flex-none" />
                                                )}
                                                <span className="max-w-[180px] truncate">{p.method ? `${p.method}: ` : ''}{p.number}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removePayment(idx)}
                                                    aria-label={t('common.delete') || 'মুছুন'}
                                                    className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full hover:bg-primary/20"
                                                >
                                                    <IconX size={11} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <div className="w-32 flex-none">
                                        <GeoCombobox
                                            value={payMethod}
                                            onChange={(val) => setPayMethod(val)}
                                            onSelectOption={(opt) => setPayMethod(opt.value)}
                                            options={PAYMENT_PRESETS.map((m) => ({ label: m, value: m }))}
                                            placeholder={t('invoiceSettings.paymentMethodPlaceholder') || 'মেথড'}
                                            className="border-zinc-200 dark:border-zinc-700 rounded-lg text-zinc-800 focus:ring-primary/40"
                                        />
                                    </div>
                                    <input
                                        value={payNumber}
                                        onChange={(e) => setPayNumber(e.target.value)}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addPayment(); } }}
                                        placeholder={t('invoiceSettings.paymentNumberPlaceholder') || 'নম্বর'}
                                        className="flex-1 min-w-0 px-3 py-2 border border-zinc-200 dark:border-zinc-700 rounded-lg text-sm bg-white dark:bg-zinc-800 text-zinc-800 dark:text-zinc-100 placeholder:text-zinc-400 focus:outline-none focus:ring-2 focus:ring-primary/40"
                                    />
                                    <button
                                        type="button"
                                        onClick={addPayment}
                                        aria-label={t('common.add') || 'যোগ'}
                                        className="flex-none inline-flex h-9 w-9 items-center justify-center rounded-lg bg-primary text-white hover:bg-primary/90 transition-colors"
                                    >
                                        <IconPlus size={16} />
                                    </button>
                                </div>
                                <p className="mt-1.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                    {t('invoiceSettings.paymentNumbersHint') || 'বিকাশ, নগদ, রকেট ইত্যাদি মেথড ও নম্বর যোগ করুন — ইনভয়েসের ফুটারে দেখাবে।'}
                                </p>
                            </div>

                            {/* Footer services — pick from the shop's services or type
                                custom, add multiple. Shown at the bottom of the invoice. */}
                            <div className="border-t border-zinc-200/60 dark:border-zinc-700/50 pt-4">
                                <h3 className="text-sm font-semibold text-zinc-800 dark:text-zinc-100 mb-2.5">
                                    {t('invoiceSettings.footerText') || 'ফুটারে সার্ভিসের নাম'}
                                </h3>
                                {(settings.footerServices?.length > 0) && (
                                    <div className="flex flex-wrap gap-1.5 mb-2">
                                        {settings.footerServices.map((svc, idx) => (
                                            <span key={`${svc}-${idx}`} className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full bg-primary/10 text-primary text-xs font-semibold">
                                                <span className="max-w-[160px] truncate">{svc}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => removeFooterService(idx)}
                                                    aria-label={t('common.delete') || 'মুছুন'}
                                                    className="inline-flex h-4 w-4 flex-none items-center justify-center rounded-full hover:bg-primary/20"
                                                >
                                                    <IconX size={11} />
                                                </button>
                                            </span>
                                        ))}
                                    </div>
                                )}
                                {/* Remount on each add (key = count) so the input clears after a pick/Enter. */}
                                <GeoCombobox
                                    key={settings.footerServices?.length || 0}
                                    value=""
                                    onChange={() => {}}
                                    onSelectOption={(opt) => addFooterService(opt.value)}
                                    onEnter={(val) => addFooterService(val)}
                                    options={footerServiceOptions}
                                    placeholder={t('invoiceSettings.footerTextPlaceholder') || 'সার্ভিস বাছুন বা লিখে Enter চাপুন'}
                                    className="border-zinc-200 dark:border-zinc-700 rounded-lg text-zinc-800 focus:ring-primary/40"
                                />
                                <p className="mt-1.5 text-[11px] text-zinc-400 dark:text-zinc-500">
                                    {t('invoiceSettings.footerTextHint') || 'তালিকা থেকে বাছুন অথবা লিখে Enter দিন। একাধিক যোগ করা যাবে। খালি রাখলে কিছু দেখাবে না।'}
                                </p>
                            </div>

                            </section>
                            </div>
                        </div>

                        {/* Live preview — true-to-size sheet for the selected paper */}
                        <div className="flex-none overflow-y-auto border-t border-zinc-100 bg-zinc-100/70 p-4 dark:border-zinc-800 dark:bg-zinc-900/40 sm:p-6 lg:w-[560px] lg:border-l lg:border-t-0">
                            <p className="mb-3 text-[11px] font-semibold uppercase tracking-wide text-zinc-400 dark:text-zinc-500">
                                {t('invoiceSettings.preview') || 'প্রিভিউ'}
                            </p>
                            <div className="mx-auto overflow-hidden rounded-lg bg-white shadow-md ring-1 ring-black/5 dark:ring-white/10" style={{ maxWidth: Math.min(500, paperPx(settings.paperSize).w) }}>
                                <InvoicePreviewFrame html={previewHtml} paper={paperPx(settings.paperSize)} reloadKey={`${template}|${settings.paperSize}`} />
                            </div>
                        </div>
                    </div>

                    {/* Footer — extra right padding so the actions clear the floating
                        support widget pinned to the viewport's bottom-right corner. */}
                    <div className="flex items-center justify-between gap-2.5 px-5 py-4 pr-5 sm:pr-24 border-t border-orange-100/70 dark:border-zinc-800 bg-gradient-to-r from-primary/10 via-primary/5 to-transparent dark:from-primary/10 dark:via-zinc-900 dark:to-zinc-900 flex-none">
                        <button
                            type="button"
                            onClick={resetAll}
                            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium text-zinc-500 dark:text-zinc-400 hover:text-zinc-700 dark:hover:text-zinc-200 transition-colors"
                        >
                            <IconRestore size={15} />
                            {t('invoiceSettings.reset') || 'রিসেট'}
                        </button>
                        <div className="flex items-center gap-2.5">
                            <button
                                type="button"
                                onClick={() => setOpen(false)}
                                className="px-4 py-2 text-sm font-semibold rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-100 dark:hover:bg-zinc-800 transition-colors"
                            >
                                {t('common.cancel') || 'বাতিল'}
                            </button>
                            <SaveButton dirty={dirty} saving={saving} onClick={apply} />
                        </div>
                    </div>
                </DialogContent>
            </Dialog>
        </>
    );
}
